import * as React from "react";
import { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Alert,
  Box,
  Typography,
  Paper,
  Skeleton,
  Stack,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useAuth } from "react-oidc-context";
import { AccountApi, Configuration } from "account-common";
import { useTranslation } from "i18n-fe";
import { PageLayout, StyledBreadcrumb } from "common-fe";

const ACCOUNT_API_BASE_URL = import.meta.env.VITE_ACCOUNT_API_BASE_URL;

export default function AccountSettings() {
  const auth = useAuth();
  const theme = useTheme();
  const { t } = useTranslation("common");
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [profileStatus, setProfileStatus] = useState({ type: "", message: "" });
  const [passwordStatus, setPasswordStatus] = useState({
    type: "",
    message: "",
  });
  const [passwordMatchError, setPasswordMatchError] = useState("");
  const [loading, setLoading] = useState(true);
  const [emailError, setEmailError] = useState("");
  const [fetchError, setFetchError] = useState(false);

  const breadcrumbs = [
    <StyledBreadcrumb key="settings" label={t("settings.accountSettingsTitle")} />
  ];

  // Fetch user info on mount
  useEffect(() => {
    const fetchAccountInfo = async () => {
      const accessToken = auth.user?.access_token;
      if (!accessToken) {
        setLoading(false);
        return;
      }
      try {
        const config = new Configuration({
          basePath: ACCOUNT_API_BASE_URL,
          accessToken: accessToken,
        });
        const accountApi = new AccountApi(config);
        const response = await accountApi.getAccountInfo();
        // If we got a response (even if unexpected format), it's not a connection error
        setFetchError(false);
        if (response.data.status === "success" && response.data.data) {
          setForm((prev) => ({
            ...prev,
            firstName: response.data.data.firstName || "",
            lastName: response.data.data.lastName || "",
            email: response.data.data.email || "",
          }));
        }
      } catch (err) {
        console.error(t('settings.failedToFetchAccountInfo'), err);
        setFetchError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchAccountInfo();
  }, [auth.user?.access_token]);

  // Helper to update form state
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // Set up the API client
  const api = React.useMemo(() => {
    const accessToken = auth.user?.access_token;
    if (!accessToken) return null;
    const config = new Configuration({
      basePath: ACCOUNT_API_BASE_URL,
      accessToken: accessToken,
    });
    return new AccountApi(config);
  }, [auth.user?.access_token]);

  // Update attributes handler
  const handleUpdateAttributes = async (e) => {
    e.preventDefault();
    setLoading(true);
    setProfileStatus({ type: "", message: "" });
    setEmailError("");
    // Email validation
    const email = form.email.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      setEmailError(t("settings.validation.emailRequiredError"));
      setLoading(false);
      return;
    }
    if (!emailRegex.test(email)) {
      setEmailError(t("settings.validation.emailInvalidError"));
      setLoading(false);
      return;
    }
    try {
      if (!api) throw new Error("No access token found.");
      const body = {
        firstName: form.firstName || undefined,
        lastName: form.lastName || undefined,
        email: form.email || undefined,
      };
      const response = await api.updateAccount(body);
      if (response.data.status === "success" && response.data.data) {
        setProfileStatus({
          type: "success",
          message: response.data.data.message || t("settings.profile.profileUpdateSuccess"),
        });
      } else {
        setProfileStatus({
          type: "error",
          message: (response.data.data && response.data.data.message) || t("settings.profile.profileUpdateError"),
        });
      }
    } catch (err) {
      setProfileStatus({ type: "error", message: err.message });
    } finally {
      setLoading(false);
    }
  };

  // Change password handler
  const handleChangePassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setPasswordStatus({ type: "", message: "" });
    setPasswordMatchError("");
    try {
      if (!api) throw new Error("No access token found.");
      if (!form.oldPassword || !form.newPassword || !form.confirmPassword) {
        throw new Error(t("settings.password.passwordFieldsRequired"));
      }
      if (form.newPassword !== form.confirmPassword) {
        setPasswordMatchError(t("settings.password.passwordMismatchError"));
        setLoading(false);
        return;
      }
      const body = {
        oldPassword: form.oldPassword,
        newPassword: form.newPassword,
      };
      const response = await api.updateAccount(body);
      if (response.data.status === "success" && response.data.data) {
        setPasswordStatus({
          type: "success",
          message: response.data.data.message || t("settings.password.passwordChangeSuccess"),
        });
        // Clear password fields after successful update
        setForm((prev) => ({
          ...prev,
          oldPassword: "",
          newPassword: "",
          confirmPassword: "",
        }));
      } else {
        setPasswordStatus({
          type: "error",
          message: (response.data.data && (response.data.data.error || response.data.data.message)) || t("settings.password.passwordChangeError"),
        });
      }
    } catch (err) {
      if (err?.response?.data?.data?.error) {
        setPasswordStatus({
          type: "error",
          message: err.response.data.data.error,
        });
      } else if (err?.response?.data?.data?.message) {
        setPasswordStatus({
          type: "error",
          message: err.response.data.data.message,
        });
      } else {
        setPasswordStatus({ type: "error", message: err.message });
      }
    } finally {
      setLoading(false);
    }
  };

  // If there was an error fetching account info, show error message
  if (fetchError) {
    return (
      <PageLayout breadcrumbs={breadcrumbs}>
        <Alert severity="error" sx={{ mb: 3 }}>
          Unable to connect to the account service
        </Alert>
        <Button
          variant="contained"
          onClick={() => window.location.reload()}
          sx={{ mt: 2 }}
        >
          Retry
        </Button>
      </PageLayout>
    );
  }

  // Show loading state
  if (loading) {
    return (
      <PageLayout breadcrumbs={breadcrumbs}>
        <Stack spacing={3}>
          <Skeleton variant="rectangular" height={200} />
          <Skeleton variant="rectangular" height={200} />
        </Stack>
      </PageLayout>
    );
  }

  return (
    <PageLayout breadcrumbs={breadcrumbs}>
      <Paper sx={{ p: 3 }}>
        <Stack spacing={4}>
          {/* Profile Section */}
          <Box>
            <Typography variant="h6" gutterBottom>
              {t("settings.profile.profileTitle")}
            </Typography>
            <Box component="form" onSubmit={handleUpdateAttributes} sx={{ mt: 2 }}>
              <Stack spacing={3}>
                <TextField
                  label={t("settings.profile.firstNameLabel")}
                  name="firstName"
                  value={form.firstName}
                  onChange={handleChange}
                  fullWidth
                  disabled={loading}
                  
                  slotProps={{
                    htmlInput: {
                      "data-testid": "profile-firstName",
                    },
                  }}
                />
                <TextField
                  label={t("settings.profile.lastNameLabel")}
                  name="lastName"
                  value={form.lastName}
                  onChange={handleChange}
                  fullWidth
                  disabled={loading}
                  slotProps={{
                    htmlInput: {
                      "data-testid": "profile-lastName",
                    },
                  }}
                />
                <TextField
                  label={t("settings.profile.emailLabel")}
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  error={!!emailError}
                  helperText={emailError}
                  fullWidth
                  required
                  disabled={loading}
                  slotProps={{
                    htmlInput: {
                      "data-testid": "profile-email",
                    },
                  }}
                />
                {profileStatus.type && (
                  <Alert severity={profileStatus.type}>
                    {profileStatus.message}
                  </Alert>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  sx={{ alignSelf: "flex-start" }}
                  data-testid="profile-updateButton"
                  slotProps={{
                    htmlButton: {
                      "data-testid": "profile-updateButton",
                    },
                  }}
                >
                  {t("settings.profile.saveButton")}
                </Button>
              </Stack>
            </Box>
          </Box>

          {/* Password Section */}
          <Box>
            <Typography variant="h6" gutterBottom>
              {t("settings.password.passwordTitle")}
            </Typography>
            <Box component="form" onSubmit={handleChangePassword} sx={{ mt: 2 }}>
              <Stack spacing={3}>
                <TextField
                  label={t("settings.password.oldPasswordLabel")}
                  name="oldPassword"
                  type="password"
                  value={form.oldPassword}
                  onChange={handleChange}
                  fullWidth
                  required
                  disabled={loading}
                  
                  slotProps={{
                    htmlInput: {
                      "data-testid": "password-oldPassword",
                    },
                  }}
                />
                <TextField
                  label={t("settings.password.newPasswordLabel")}
                  name="newPassword"
                  type="password"
                  value={form.newPassword}
                  onChange={handleChange}
                  fullWidth
                  required
                  disabled={loading}
                  slotProps={{
                    htmlInput: {
                      "data-testid": "password-newPassword",
                    },
                  }}
                />
                <TextField
                  label={t("settings.password.confirmPasswordLabel")}
                  name="confirmPassword"
                  type="password"
                  value={form.confirmPassword}
                  onChange={handleChange}
                  error={!!passwordMatchError}
                  helperText={passwordMatchError}
                  fullWidth
                  required
                  disabled={loading}
                  slotProps={{
                    htmlInput: {
                      "data-testid": "password-confirmPassword",
                    },
                  }}
                />
                {passwordStatus.type && (
                  <Alert severity={passwordStatus.type}>
                    {passwordStatus.message}
                  </Alert>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  sx={{ alignSelf: "flex-start" }}
                  data-testid="password-changeButton"
                >
                  {t("settings.password.changePasswordButton")}
                </Button>
              </Stack>
            </Box>
          </Box>
        </Stack>
      </Paper>
    </PageLayout>
  );
}
